import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { AuthenticationProvider, useAuthentication } from '@zenbusiness/application-commons-authentication';
import { CSChatBotProvider } from '@zenbusiness/application-commons-cs-chatbot';
import { SlideCheckoutProvider } from '@zenbusiness/application-commons-ecommerce';
import { useEnv } from '@zenbusiness/application-commons-env';
import { ExperimentsProvider } from '@zenbusiness/application-commons-experiments';
import { GraphQLProvider } from '@zenbusiness/application-commons-graphql';
import { IdentityProvider } from '@zenbusiness/application-commons-identity';
import { IntlProvider } from '@zenbusiness/application-commons-intl';
import { LoadingZone, SuspenseZone } from '@zenbusiness/application-commons-loading';
import { LoggingProvider, ErrorBoundary } from '@zenbusiness/application-commons-logging';
import { VisitProvider } from '@zenbusiness/application-commons-visit';
import { WebView } from '@zenbusiness/application-commons-webview';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';
import { ZuiThemeProvider } from '@zenbusiness/zui/styles';

import { DEFAULT_SEGMENT, NAME, RELEASE_VERSION } from '#client/build-env';
import { BootstrapError } from '#client/components/BootstrapError';
import { intlFormats } from '#client/constants';
import { login, logout, callback } from '#client/constants/routes';
import { useEnterprise } from '#client/hooks/useEnterprise';

import globalGraphQLErrorHandler from './GraphQLErrorHandler';

const emotionCache = createCache({
  key: NAME,
});

// Passes the enterprise-specific NICE Guide template at init so Squarespace users
// get their co-branded chat; templateID stays undefined for ZB/unknown so URL rules apply.
// key forces remount when templateID resolves after auth so Guide re-inits with the template.
export function DashboardChatBotProvider({ children }) {
  const { authenticated } = useAuthentication();
  const { enterprise } = useEnterprise();
  const { CS_CHATBOT_CLIENT_ID: tenantId } = useEnv('head-first');
  const templateID = enterprise?.chatTemplateId;
  const chatBotKey = templateID ?? 'no-template';

  // Guide init runs once on mount inside CSChatBotProvider. Defer until auth resolves
  // so enterpriseId (and templateID) are stable before the first guide init call.
  if (!authenticated) {
    return children;
  }

  return (
    <CSChatBotProvider key={chatBotKey} hideDefaultLauncher tenantId={tenantId} templateID={templateID}>
      {children}
    </CSChatBotProvider>
  );
}

// eslint-disable-next-line react/prop-types
export default function Providers({ children, federated }) {
  const {
    AUTH0_AUDIENCE,
    AUTH0_CLIENTID,
    AUTH0_CONNECTION,
    AUTH0_DOMAIN,
    GRAPHQL_ROUTE,
    LOCAL_LOGIN_APP_ORIGIN,
    OIDC_CLIENT_ID,
    OIDC_LOGOUT_ENABLED,
    OIDC_LOGIN_ENABLED,
    USE_ZB_SESSION_INFO,
    IS_LOCAL,
    AUTH_PATHS,
    DEPLOY_ENV,
  } = useEnv('head-first'); // use MFE values
  const { LD_CLIENT_ID, LOCALE } = useEnv('root-first'); // use parent values

  return (
    <LoadingZone fallback={federated ? null : <PageLoading />} loadId="App/Providers">
      <LoggingProvider
        tag-frontend={NAME}
        tag-frontend-release={`${NAME}-${RELEASE_VERSION || 'unset'}`}
        tag-isWebView={String(WebView?.isWebView)}
      >
        <ErrorBoundary fallback={<BootstrapError />} id={`${NAME}/App/Providers/bootstrap`}>
          <CacheProvider value={emotionCache}>
            <ZuiThemeProvider baseline={!federated} scopedBaseline={federated}>
              <ErrorBoundary fallback={<PageError />} id={`${NAME}/App/Providers`}>
                <ExperimentsProvider deployEnv={DEPLOY_ENV} appId={NAME} clientId={LD_CLIENT_ID}>
                  <AuthenticationProvider
                    clientID={AUTH0_CLIENTID}
                    domain={AUTH0_DOMAIN}
                    audience={AUTH0_AUDIENCE}
                    connection={AUTH0_CONNECTION}
                    loginUri={login}
                    logoutUri={logout}
                    callbackUri={callback}
                    logoutReturnUri="/"
                    enableOidcLogout={OIDC_LOGOUT_ENABLED}
                    oidcClientId={OIDC_CLIENT_ID}
                    enableOidcLogin={OIDC_LOGIN_ENABLED}
                    useZbSessionInfo={USE_ZB_SESSION_INFO}
                    localLoginAppOrigin={LOCAL_LOGIN_APP_ORIGIN}
                    userType="customer"
                    isLocal={IS_LOCAL}
                    {...AUTH_PATHS}
                  >
                    <GraphQLProvider url={GRAPHQL_ROUTE} onError={globalGraphQLErrorHandler}>
                      <VisitProvider disableCreation>
                        <IdentityProvider>
                          <SuspenseZone loadId={`${NAME}/App/Providers#Intl`}>
                            <IntlProvider
                              locale={LOCALE}
                              findPath={`${DEFAULT_SEGMENT}/messages/<%= locale %>/${RELEASE_VERSION}`}
                              defaultLocale="en-US"
                              formats={intlFormats}
                              defaultFormats={intlFormats}
                            >
                              {federated ? (
                                <SlideCheckoutProvider>{children}</SlideCheckoutProvider>
                              ) : (
                                <DashboardChatBotProvider>
                                  <SlideCheckoutProvider>{children}</SlideCheckoutProvider>
                                </DashboardChatBotProvider>
                              )}
                            </IntlProvider>
                          </SuspenseZone>
                        </IdentityProvider>
                      </VisitProvider>
                    </GraphQLProvider>
                  </AuthenticationProvider>
                </ExperimentsProvider>
              </ErrorBoundary>
            </ZuiThemeProvider>
          </CacheProvider>
        </ErrorBoundary>
      </LoggingProvider>
    </LoadingZone>
  );
}
